/** TemperaturesBlockSlot — three temp inputs + cold-chain reminder
 *  + compliance-temp confirm + nested Trailer-clean Y/N (cold-chain only)
 *  + nested Contamination Y/N + types (cold-chain only).
 *
 *  Extracted verbatim from QATab.jsx — including the auto-NCR hints. */
import { Input } from "@/components/ui/input";
import { Field, MultiSelectChips } from "../_shared";
import { useSlotLabel } from "../hooks/useSlotLabels";

const COLD_CHAIN_RANGES = {
  chill:  "+2 °C to +8 °C",
  frozen: "≤ -18 °C",
};

const CONTAMINATION_OPTIONS = [
  { value: "glass",        label: "Glass" },
  { value: "hard_plastic", label: "Hard plastic" },
  { value: "allergens",    label: "Allergens" },
  { value: "pests",        label: "Pests" },
  { value: "animals",      label: "Animals" },
  { value: "chemicals",    label: "Chemicals" },
];

export default function TemperaturesBlockSlot({ values, set }) {
  const isColdChain = values.cold_chain === "chill" || values.cold_chain === "frozen";
  const lbl = useSlotLabel("qa.temperatures");

  return (
    <div className="rounded-md border border-sky-200 bg-sky-50/40 p-3 space-y-2" data-testid="temp-block">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <div className="text-xs uppercase tracking-wider text-sky-700 font-medium">
          {lbl("section_title", "Temperatures (°C)")}
        </div>
        {COLD_CHAIN_RANGES[values.cold_chain] && (
          <div className="text-[11px] text-sky-700/80" data-testid="temp-cold-chain-reminder">
            Reminder · cold chain: <strong>{values.cold_chain}</strong> ·
            expected <strong>{COLD_CHAIN_RANGES[values.cold_chain]}</strong>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={lbl("between_cartons", "Pallet temp between cartons")}>
          <Input type="number" step="0.1" value={values.pallet_temp_between_cartons_c}
                 onChange={(e) => set("pallet_temp_between_cartons_c", e.target.value)}
                 data-testid="pallet-temp-between-cartons-input" />
        </Field>
        <Field label={lbl("sample_1", "Sample pallet #1 temp")}>
          <Input type="number" step="0.1" value={values.sample_pallet1_temp_c}
                 onChange={(e) => set("sample_pallet1_temp_c", e.target.value)}
                 data-testid="sample-pallet1-temp-input" />
        </Field>
        <Field label={lbl("sample_2", "Sample pallet #2 temp")}>
          <Input type="number" step="0.1" value={values.sample_pallet2_temp_c}
                 onChange={(e) => set("sample_pallet2_temp_c", e.target.value)}
                 data-testid="sample-pallet2-temp-input" />
        </Field>
      </div>
      <label className="flex items-start gap-2 text-sm pt-1">
        <input type="checkbox" className="mt-0.5"
               checked={!!values.compliance_temp_confirmed}
               onChange={(e) => set("compliance_temp_confirmed", e.target.checked)}
               data-testid="compliance-temp-confirmed" />
        <span>
          <strong>I confirm</strong> the recorded temperatures match the
          cold-chain requirement for this booking.
        </span>
      </label>

      {isColdChain && (
        <div className="pt-2 border-t border-sky-200/60" data-testid="trailer-clean-block">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Trailer clean &amp; tidy?
          </div>
          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="trailer-clean-confirmed"
                     checked={values.trailer_clean_confirmed === true}
                     onChange={() => set("trailer_clean_confirmed", true)}
                     data-testid="trailer-clean-yes" />
              <span>Yes</span>
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="trailer-clean-confirmed"
                     checked={values.trailer_clean_confirmed === false}
                     onChange={() => set("trailer_clean_confirmed", false)}
                     data-testid="trailer-clean-no" />
              <span>No</span>
            </label>
          </div>
          {values.trailer_clean_confirmed === false && (
            <p className="text-[11px] text-rose-700 pt-1" data-testid="trailer-clean-ncr-hint">
              A &quot;No&quot; opens an NCR (issue type: <em>Vehicle</em>) on save.
            </p>
          )}
        </div>
      )}

      {isColdChain && (
        <div className="pt-2 border-t border-sky-200/60 space-y-2" data-testid="contamination-block">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Any glass, hard plastic, allergens, pests, animals, chemicals?
          </h4>
          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="contamination-present"
                     checked={values.contamination_present === true}
                     onChange={() => set("contamination_present", true)}
                     data-testid="contamination-present-yes" />
              <span>Yes</span>
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="contamination-present"
                     checked={values.contamination_present === false}
                     onChange={() => {
                       set("contamination_present", false);
                       set("contamination_types", []);
                     }}
                     data-testid="contamination-present-no" />
              <span>No</span>
            </label>
          </div>
          {values.contamination_present && (
            <>
              <MultiSelectChips
                label="Which?"
                testid="contamination-types"
                options={CONTAMINATION_OPTIONS}
                value={values.contamination_types}
                onChange={(arr) => set("contamination_types", arr)}
              />
              <p className="text-[11px] text-rose-700" data-testid="contamination-ncr-hint">
                A &quot;Yes&quot; answer will automatically open an NCR (issue type:
                <em> Food safety</em>) when this booking is saved.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
